
import { defineStore } from "pinia";
import Dexie from "dexie";
import { markRaw } from "vue";
import { api, BASE_URL } from "../services/api.js";
import { getOrCreateDeviceId } from "../services/platform.js";
import i18n from "@/i18n";

const t = (key, params) => i18n.global.t(`dbStore.${key}`, params);

// Chiavi localStorage per stato persistente sync
const LS_SYNC_STATUS = "poderi2_db_sync_status"; // idle | clearing | fetching | populating | done
const LS_SYNC_SCHEMA = "poderi2_db_schema";      // hash dello schema usato nell'ultimo sync completato
const LS_RECOVERY_COUNT = "poderi2_db_recovery";  // contatore anti-loop recovery (reset a 0 quando sync va a buon fine)
const LS_FARM_SYNC_MAP = "poderi2_farm_sync_map";       // { farmId: last_sync_at ISO, ... }
const LS_LAST_VOCAB_SYNC = "poderi2_db_last_vocab_sync"; // timestamp ultimo download vocabolari

// [TEMP DEBUG - indagine memory leak, rimuovere a fine indagine]
// Logger diagnostico durata chiamate + heap JS. Attivare con:
//   localStorage.setItem('ble_debug_perf', '1')
function _perfDebugEnabled() {
  return localStorage.getItem("ble_debug_perf") === "1";
}
function _perfLog(label, t0) {
  if (!_perfDebugEnabled()) return;
  const ms = (performance.now() - t0).toFixed(1);
  const mem = performance.memory
    ? ` heap=${(performance.memory.usedJSHeapSize / 1048576).toFixed(1)}MB/${(performance.memory.totalJSHeapSize / 1048576).toFixed(1)}MB`
    : "";
  console.log(`[PERF] ${label}: ${ms}ms${mem}`);
}
// [/TEMP DEBUG]

function getFarmSyncMap() {
  try {
    return JSON.parse(localStorage.getItem(LS_FARM_SYNC_MAP) || '{}');
  } catch { return {}; }
}
function saveFarmSyncMap(map) {
  localStorage.setItem(LS_FARM_SYNC_MAP, JSON.stringify(map));
}

function getSyncStatus() {
  return localStorage.getItem(LS_SYNC_STATUS) || "idle";
}
function setSyncStatus(status) {
  localStorage.setItem(LS_SYNC_STATUS, status);
}
function getSchemaHash(config) {
  // Hash semplice: nomi tabelle ordinati
  return Object.keys(config).sort().join(",");
}
function getRecoveryCount() {
  return parseInt(localStorage.getItem(LS_RECOVERY_COUNT) || "0", 10);
}
function setRecoveryCount(n) {
  localStorage.setItem(LS_RECOVERY_COUNT, String(n));
}
// failed del 207 di /sync: { tabella: [pk] } dei record non salvati dal server.
// null nella lista = PK non nota al server → tutta la tabella conta come rifiutata
function isRejected(failed, table, key) {
  const pks = failed?.[table];
  return Array.isArray(pks) && (pks.includes(key) || pks.includes(null));
}

export const useDbStore = defineStore("db", {
  state: () => ({
    db: null,
    isReady: false,
    isSynced: false,
    isEmpty: true,
    isLoading: false,
    errors: [],

    // true se il DB è stato trovato corrotto e serve un re-sync
    needsResync: false,

    // ID univoco per questo tab/dispositivo (per evitare re-sync sul tab che ha inviato)
    tabId: Math.random().toString(36).substring(2, 10),

    // Sync state
    syncTimer: null,
    syncInProgress: false,
    pendingSyncCountInProgress: false, // [TEMP DEBUG] guard anti-sovrapposizione getPendingSyncCount
    syncDebounceMs: 2000, // 2 secondi di debounce
    lastSyncTime: null,
    lastSyncId: null, // ultimo sync_id inviato da questo tab

    // SSE stream state
    streamConnection: null,
    streamConnected: false,
    streamReconnectTimer: null,
    streamReconnectDelay: 2000,   // delay iniziale (ms) — raddoppia ad ogni errore
    streamErrorCount: 0,          // contatore errori consecutivi per exponential backoff

    // Ultimo evento SSE ricevuto (utile per UI/notifiche)
    lastStreamEvent: null,
    streamEventCount: 0,

    // Sync status tracking (per UI e resync intelligente)
    farmSyncMap: getFarmSyncMap(),                                      // { farmId: last_sync_at } per-azienda
    lastVocabSync: localStorage.getItem(LS_LAST_VOCAB_SYNC) || null,  // timestamp ultimo download vocabolari

    // Resync check state
    syncCheckInProgress: false,
    syncCheckTimer: null,
    onlineListenerAttached: false,

    // Salvati da startSyncWatchers per uso interno (full resync)
    _syncConfig: null,
    _fetchDataFn: null,

    // Errore di sincronizzazione da mostrare all'utente (null = nessun errore)
    // { status: 401, message: 'Non è stato possibile...' }
    syncError: null,

    // true durante syncDbFromBackend (full sync): blocca checkForNewFarmCenters
    isSyncingFull: false,
  }),

  actions: {
    /**
     * Inizializza il database Dexie
     * @param {Object} config - Oggetto Dexie stores schema, es: { users: '++id,name', posts: '++id,userId,title' }
     * @param {number} version - Versione del db
     */
    async init(config, version = 1) {
      console.log("DB INIT with config:", config);

      this.errors = [];
      this.needsResync = false;

      try {
        const newSchemaHash = getSchemaHash(config);
        const savedSchemaHash = localStorage.getItem(LS_SYNC_SCHEMA) || "";
        const syncStatus = getSyncStatus();

        // Se il sync precedente non si è completato (status != done),
        // oppure lo schema è cambiato, forza cancellazione e re-sync
        const syncWasInterrupted = syncStatus !== "idle" && syncStatus !== "done";
        const schemaChanged = savedSchemaHash !== "" && savedSchemaHash !== newSchemaHash;

        if (syncWasInterrupted || schemaChanged) {
          console.warn(`DB: stato precedente anomalo (syncStatus=${syncStatus}, schemaChanged=${schemaChanged}). Cancellazione DB.`);
          if (this.db && this.db.isOpen()) {
            this.db.close();
          }
          this.db = null;
          await Dexie.delete("Poderi2DB");
          setSyncStatus("idle");
          this.needsResync = true;
        }

        // Confronta lo schema richiesto con quello attuale (se db già aperto)
        if (this.db) {
          const currentTables = this.db.tables.map((t) => t.name).sort().join(",");

          if (currentTables === newSchemaHash) {
            // Schema invariato — verifica integrità reale
            const healthy = await this._verifyIntegrity();
            if (healthy) {
              console.log("DB già inizializzato con lo stesso schema, skip");
              return { ok: true, data: true };
            }
            // DB corrotto: cancella e ricrea
            console.warn("DB: integrità fallita, ricreazione");
            this.db.close();
            this.db = null;
            await Dexie.delete("Poderi2DB");
            setSyncStatus("idle");
            this.needsResync = true;
          } else {
            // Schema cambiato: cancella e ricrea
            console.log(`Schema cambiato (${currentTables} -> ${newSchemaHash}), ricreazione DB`);
            if (this.db.isOpen()) {
              this.db.close();
            }
            await Dexie.delete("Poderi2DB");
            setSyncStatus("idle");
            this.needsResync = true;
          }
        }

        this.db = markRaw(new Dexie("Poderi2DB"));
        this.db.version(version).stores(config);

        await this.db.open();

        console.log("DB aperto:", this.db.isOpen());
        console.log(
          "Tabelle:",
          this.db.tables.map((t) => t.name)
        );

        // Verifica integrità reale (transazione IDB di test)
        const healthy = await this._verifyIntegrity();
        if (!healthy) {
          console.warn("DB appena aperto ma integrità fallita. Cancellazione e retry.");
          this.db.close();
          this.db = null;
          await Dexie.delete("Poderi2DB");
          setSyncStatus("idle");

          // Riprova una volta sola
          this.db = markRaw(new Dexie("Poderi2DB"));
          this.db.version(version).stores(config);
          await this.db.open();
          this.needsResync = true;
        }

        this.isReady = true;

        // Controlla se la tabella farm_center esiste e ha record
        try {
          const tableExists = this.db.tables.some(t => t.name === "farm_center");
          if (tableExists) {
            const count = await this.db.table("farm_center").count();
            this.isEmpty = count === 0;
            console.log("farm_center count:", count);
          } else {
            this.isEmpty = true;
          }
        } catch (err) {
          console.warn("Impossibile verificare farm_center:", err.message);
          this.isEmpty = true;
        }

        // Se il DB è vuoto e lo schema ha tabelle, serve un re-sync
        if (this.isEmpty && Object.keys(config).length > 0) {
          const prevStatus = getSyncStatus();
          if (prevStatus !== "done") {
            this.needsResync = true;
          }
        }

        return { ok: true, data: true };
      } catch (e) {
        console.error("Errore inizializzazione DB:", e);
        this.errors.push(e.message);
        this.isReady = false;
        this.needsResync = true;
        return { ok: false, message: e.message };
      }
    },

    /**
     * Verifica che le tabelle Dexie esistano realmente in IndexedDB
     * tramite una transazione di test. Questo cattura il caso in cui
     * Dexie ha lo schema in memoria ma IDB non ha gli object store.
     * @returns {boolean}
     */
    async _verifyIntegrity() {
      if (!this.db || !this.db.isOpen()) return false;

      try {
        // Prova una transazione readonly su tutte le tabelle
        const tableNames = this.db.tables.map(t => t.name);
        if (tableNames.length === 0) return true;

        // Prende la prima tabella e prova un count — questo forza IDB ad aprire
        // una transazione reale sull'object store
        const testTable = this.db.table(tableNames[0]);
        await testTable.count();
        return true;
      } catch (e) {
        console.warn("DB integrity check failed:", e.message);
        return false;
      }
    },

    /**
     * Assicura che il DB sia aperto prima di operazioni
     */
    async ensureOpen() {
      if (!this.db) {
        throw new Error("DB non inizializzato. Chiama init() prima.");
      }

      if (!this.db.isOpen()) {
        console.warn("DB chiuso, riapertura...");
        await this.db.open();
      }
    },

    /**
     * Sincronizza il db locale con i dati dal backend
     * @param {string} profileId - id del profilo/azienda attivo
     * @param {Object} config - schema Dexie (tabella: definizione)
     * @param {Function} fetchData - funzione async che ritorna i dati da inserire (es: chiama API)
     */
    async syncDbFromBackend(profileId, config, fetchData) {
      this.isLoading = true;
      this.isSynced = false;
      this.isEmpty = true;
      this.errors = [];
      this.isSyncingFull = true;

      try {
        if (!this.db || !this.isReady) {
          const initResult = await this.init(config);
          if (!initResult.ok || !this.db) {
            throw new Error(t('cannotInitDb', { message: initResult.message || "db null" }));
          }
        }

        let start_time = Date.now();
        console.log(`Sync DB da backend per profilo ${profileId}...`);

        await this.ensureOpen();

        // FASE 1: Segna stato "clearing" prima di svuotare
        setSyncStatus("clearing");

        const clearPromises = this.db.tables.map((table) => table.clear());
        await Promise.all(clearPromises);

        console.log("Tabelle svuotate" + ` (${Date.now() - start_time} ms)`);

        // FASE 2: Segna stato "fetching" prima di scaricare dati
        setSyncStatus("fetching");

        const res = await fetchData();

        console.log("Sync DB result:" + ` (${Date.now() - start_time} ms)`, res);

        if (res && res.ok && res.tables) {
          // FASE 3: Segna stato "populating" prima di inserire
          setSyncStatus("populating");

          for (const table of this.db.tables) {
            const records = res.tables[table.name];
            if (Array.isArray(records) && records.length > 0) {

              // Espandi campo json in ogni record
              for (let i = 0; i < records.length; i++) {
                let rec = records[i];
                if ("json" in rec) {
                  try {
                    let extra_data = rec["json"];
                    if (typeof extra_data === "string") {
                      extra_data = JSON.parse(extra_data);
                    }
                    records[i] = { ...extra_data, ...rec };
                    delete records[i]["json"];
                  } catch (e) {
                    console.warn(`Errore parsing extra_data per tabella ${table.name} record index ${i}:`, e);
                  }
                }
              }

              try {
                await table.bulkPut(records);
                console.log(
                  `${table.name}: ${records.length} record inseriti`
                );
              } catch (e) {
                this.errors.push(
                  `Errore inserimento bulk nella tabella ${table.name}: ${e.message}`
                );
                console.error(
                  `Errore inserimento bulk nella tabella ${table.name}:`,
                  e
                );
              }
            }
          }

          // FASE 4: Sync completato — salva stato persistente
          setSyncStatus("done");
          localStorage.setItem(LS_SYNC_SCHEMA, getSchemaHash(config));
          setRecoveryCount(0); // Reset contatore recovery

          // Salva last_sync_at per-azienda dalla risposta (tracking sync)
          if (res.server_time) {
            const map = {};
            const fcRecords = res.tables?.farm_center || [];
            for (const fc of fcRecords) {
              map[fc.id_farm_center] = fc.last_sync_at || res.server_time;
            }
            saveFarmSyncMap(map);
            this.farmSyncMap = map;
            // Full sync → aggiorna anche vocabolari
            localStorage.setItem(LS_LAST_VOCAB_SYNC, res.server_time);
            this.lastVocabSync = res.server_time;
          }

          console.log("Sync DB completato" + ` (${Date.now() - start_time} ms)`);

          this.isSynced = true;
          this.isEmpty = false;
          this.needsResync = false;
        } else {
          // Risposta non valida — il DB è vuoto
          setSyncStatus("idle");
          this.isSynced = false;
          this.isEmpty = true;

          // Segnala l'errore all'utente (es. 401 se il token non è valido)
          const status = res?._status ?? null;
          const message = status === 401
            ? t('fetchDataUnauthorized')
            : `${t('fetchDataFailed')}${status ? ` ${t('errorCode', { status })}` : ''} ${res?.message ?? ''}`.trim();
          console.error('syncDbFromBackend: risposta non valida', res);
          this.syncError = { status, message };
        }
      } catch (error) {
        console.error("Errore sync DB:", error);
        this.errors.push(`Errore sincronizzazione: ${error.message}`);
        this.isSynced = false;
        // Lascia syncStatus com'è — al prossimo boot verrà rilevato come interrotto
      } finally {
        this.isLoading = false;
        this.isSyncingFull = false;
      }
    },

    /**
     * Tenta il recovery automatico del DB.
     * Ritorna true se il recovery è stato avviato, false se non necessario o max retry raggiunto.
     * @param {Object} config - schema Dexie
     * @param {Function} fetchData - funzione per scaricare dati
     * @param {string} profileId - profilo attivo
     * @param {number} maxRetries - max tentativi per sessione (default 1)
     */
    async attemptRecovery(config, fetchData, profileId, maxRetries = 1) {
      const count = getRecoveryCount();
      if (count >= maxRetries) {
        console.warn(`DB recovery: max tentativi raggiunto (${count}/${maxRetries}). Serve intervento manuale.`);
        return false;
      }

      console.warn(`DB recovery: tentativo ${count + 1}/${maxRetries}`);
      setRecoveryCount(count + 1);

      // Cancella e ricrea da zero
      try {
        if (this.db && this.db.isOpen()) {
          this.db.close();
        }
        this.db = null;
        this.isReady = false;
        await Dexie.delete("Poderi2DB");
        setSyncStatus("idle");

        const initResult = await this.init(config);
        if (!initResult.ok || !this.db) {
          console.error("DB recovery: init fallito", initResult.message);
          return false;
        }

        await this.syncDbFromBackend(profileId, config, fetchData);
        return this.isSynced;
      } catch (e) {
        console.error("DB recovery fallito:", e);
        this.errors.push(`Recovery fallito: ${e.message}`);
        return false;
      }
    },

    /**
     * Reset manuale del contatore recovery (es. da UI debug)
     */
    resetRecoveryCount() {
      setRecoveryCount(0);
    },

    /**
     * Seleziona tutti i record da una tabella
     * @param {string} table
     * @param {Object} [where] - opzionale, filtra i risultati
     * @returns {Promise<Array>}
     */
    async select(table, where = null, skip_soft_deleted = true) {
      const _t0 = performance.now(); // [TEMP DEBUG]
      try {
        await this.ensureOpen();
        let coll = await this.db.table(table).toArray();
        if (where) {
          for (const [field, value] of Object.entries(where)) {
            coll = coll.filter((item) => item[field] === value);
          }
        }
        if (skip_soft_deleted) {
          coll = coll.filter((item) => !item._deleted);
        }
        const data = coll;
        _perfLog(`select ${table} (${data.length} rec)`, _t0); // [TEMP DEBUG]
        return { ok: true, data };
      } catch (e) {
        console.error(`Errore select su ${table}:`, e);

        // Se l'errore è NotFoundError, il DB è corrotto
        if (e.name === "NotFoundError") {
          this.needsResync = true;
        }

        return { ok: false, message: e.message };
      }
    },

    /**
     * Seleziona un record per chiave primaria
     * @param {string} table
     * @param {any} key
     * @returns {Promise<Object|null>}
     */
    async selectOne(table, key) {
      try {

        await this.ensureOpen();

        const data = await this.db.table(table).get(key);
        return { ok: true, data };
      } catch (e) {
        console.error(`Errore selectOne su ${table}:`, e);

        if (e.name === "NotFoundError") {
          this.needsResync = true;
        }

        return { ok: false, message: e.message };
      }
    },

    /**
     * Inserisce un record
     * @param {string} table
     * @param {Object} data
     * @returns {Promise<any>} - chiave primaria inserita
     */
    async insert(table, data) {
      const _t0 = performance.now(); // [TEMP DEBUG]
      try {
        await this.ensureOpen();

        data._to_synch = true;

        //clone data to avoid Vue2 reactivity issues
        data = JSON.parse(JSON.stringify(data));

        //verifica che l'oggetto abbia la chiave
        let pk = "id_" + table;
        if (!(pk in data)) {
          //genere
          data[pk] = this.generateGuid();
        }

        const key = await this.db.table(table).add(data);
        this.scheduleSyncToServer(); // Pianifica sync automatica
        _perfLog(`insert ${table}`, _t0); // [TEMP DEBUG]
        return { ok: true, data: key };
      } catch (e) {
        console.error(`Errore insert su ${table}:`, e);
        return { ok: false, message: e.message };
      }
    },

    /**
     * Aggiorna un record per chiave primaria
     * @param {string} table
     * @param {any} key
     * @param {Object} changes
     * @returns {Promise<number>} - numero di record modificati
     */
    async update(table, key, changes) {
      const _t0 = performance.now(); // [TEMP DEBUG]
      try {
        await this.ensureOpen();



        // Verifica che la tabella esista
        const tableExists = this.db.tables.some((t) => t.name === table);
        if (!tableExists) {
          throw new Error(`Tabella "${table}" non esiste`);
        }


        //clone changes to avoid Vue2 reactivity issues
        changes = JSON.parse(JSON.stringify(changes));

        changes._to_synch = true;

        // Dexie.update already returns the number of updated records (1 if found, 0 if not)
        // and is internally transactional.
        const result = await this.db.table(table).update(key, changes);


        if (result === 0) {
          console.warn(`! Update ${table}[${key}]: nessun record aggiornato (non trovato o nessun cambiamento)`);
        } else {
          console.log(`Update ${table}[${key}]: ${result} record(s) aggiornati`);
        }

        this.scheduleSyncToServer(); // Pianifica sync automatica
        _perfLog(`update ${table}`, _t0); // [TEMP DEBUG]
        return { ok: true, data: result };
      } catch (e) {
        console.error(`Errore update su ${table}:`, e);
        return { ok: false, message: e.message };
      }
    },

    /**
     * Cancella un record per chiave primaria
     * @param {string} table
     * @param {any} key
     * @returns {Promise<void>}
     */
    async deletePermanently(table, key) {
      try {
        await this.ensureOpen();

        const record = await this.db.table(table).get(key);
        if (record?.voice_source) {
          await this._unlinkVoiceSource(record.voice_source, table, key);
        }

        await this.db.table(table).delete(key);
        console.log(`Delete ${table}[${key}]`);
        return { ok: true, data: true };
      } catch (e) {
        console.error(`Errore delete su ${table}:`, e);
        return { ok: false, message: e.message };
      }
    },

    async delete(table, key) {
      try {
        await this.ensureOpen();

        const record = await this.db.table(table).get(key);
        if (record?.voice_source) {
          await this._unlinkVoiceSource(record.voice_source, table, key);
        }

        // Marca il record come da sincronizzare per cancellazione
        const changes = { _to_synch: true, _deleted: true };
        const result = await this.db.table(table).update(key, changes);

        if (result === 0) {
          console.warn(`! Delete ${table}[${key}]: nessun record aggiornato (non trovato)`);
        } else {
          console.log(`Delete ${table}[${key}]: marcato per cancellazione`);
        }

        this.scheduleSyncToServer(); // Pianifica sync automatica
        return { ok: true, data: result };
      } catch (e) {
        console.error(`Errore delete su ${table}:`, e);
        return { ok: false, message: e.message };
      }
    },

    /**
     * Rimuove il riferimento created_id/created_table dell'item collegato a un
     * record vocale (poderi.operation.voice_parsed_json) quando il record figlio
     * (operation/observation/product) viene cancellato. Non tocca created record
     * mai modificati (update non invalida il link, solo la cancellazione).
     * @param {{table: string, id: string}} voiceSource
     * @param {string} childTable
     * @param {any} childKey
     */
    async _unlinkVoiceSource(voiceSource, childTable, childKey) {
      if (!voiceSource?.table || !voiceSource?.id) return;
      try {
        const parent = await this.db.table(voiceSource.table).get(voiceSource.id);
        if (!parent?.voice_parsed_json) return;

        const parsed = typeof parent.voice_parsed_json === 'string'
          ? JSON.parse(parent.voice_parsed_json)
          : parent.voice_parsed_json;

        let changed = false;
        for (const section of ['operations', 'observations', 'warehouse_inserts']) {
          if (!Array.isArray(parsed[section])) continue;
          parsed[section] = parsed[section].map((item) => {
            if (item.created_table === childTable && String(item.created_id) === String(childKey)) {
              changed = true;
              return { ...item, created_id: null, created_table: null };
            }
            return item;
          });
        }

        if (changed) {
          await this.db.table(voiceSource.table).update(voiceSource.id, {
            voice_parsed_json: JSON.stringify(parsed),
            _to_synch: true,
          });
          this.scheduleSyncToServer();
        }
      } catch (e) {
        console.error('Errore pulizia voice_source:', e);
      }
    },

    /**
     * Sincronizza i record modificati con il server
     * @param {boolean} force - Se true, forza la sincronizzazione anche se già in corso
     * @returns {Promise<Object>} - Risultato della sincronizzazione
     */
    async syncToServer(force = false) {
      const _t0 = performance.now(); // [TEMP DEBUG]
      if (this.syncInProgress && !force) {
        console.log('Sync già in corso, skip');
        return { ok: true, skipped: true };
      }

      try {
        this.syncInProgress = true;
        await this.ensureOpen();

        this.errors = [];

        // Trova tutti i record con _to_synch = true
        // ESCLUDI image_upload_queue: è una tabella locale-only con payload base64 enormi.
        // Le immagini vengono inviate separatamente dopo il sync, tramite l'evento poderi:sync-complete.
        // ESCLUDI chat2_*: hanno il loro meccanismo di sync tramite chatStore → /api/v2/chat/...
        const EXCLUDED_FROM_SYNC = new Set(['image_upload_queue', 'chat2_room', 'chat2_message', 'chat2_message_read']);
        const tablesToSync = [];
        const _scanT0 = performance.now(); // [TEMP DEBUG]

        for (const table of this.db.tables) {
          if (EXCLUDED_FROM_SYNC.has(table.name)) continue;
          try {
            const records = await table.toArray();
            const recordsToSync = records.filter((r) => r._to_synch);
            if (recordsToSync.length > 0) {
              tablesToSync.push({ table: table.name, records: recordsToSync });
            }
          } catch (e) {
            // Ignora tabelle senza indice su _to_synch
            console.warn(`Tabella ${table.name} non ha indice su _to_synch, skip`);
          }
        }
        _perfLog(`syncToServer scan ${this.db.tables.length} tabelle`, _scanT0); // [TEMP DEBUG]

        if (tablesToSync.length === 0) {
          console.log('Nessun dato da sincronizzare');
          // Segnala comunque che le tabelle sono in sync → le immagini in coda possono essere inviate
          window.dispatchEvent(new CustomEvent('poderi:sync-complete'));
          _perfLog('syncToServer (nessun dato)', _t0); // [TEMP DEBUG]
          return { ok: true, synced: 0 };
        }

        console.log(`Sincronizzazione di ${tablesToSync.length} tabelle...`);

        // Invia al server (con tabId per evitare re-sync su questo tab)
        const res = await api.post('/api/v2/poderi2/sync', { tables: tablesToSync, tab_id: this.tabId, device_id: getOrCreateDeviceId() });

        // Record salvati dal server: tutti con ok; con il 207 tutti tranne quelli in res.failed.
        // Un 207 senza failed (server non aggiornato) o un altro errore → nessuno, restano _to_synch.
        const failed = !res.ok && res.failed && typeof res.failed === 'object' ? res.failed : null;
        if (res.ok || failed) {
          const syncedFarms = new Set();
          const failedFarms = new Set();
          // Rimuovi il flag _to_synch dai record sincronizzati
          for (const { table, records } of tablesToSync) {
            const tableObj = this.db.table(table);
            const primKey = tableObj.schema.primKey.keyPath;

            for (const record of records) {
              const key = record[primKey];

              if (isRejected(failed, table, key)) {
                if (record.id_farm_center) failedFarms.add(record.id_farm_center);
                continue;
              }
              if (record.id_farm_center) syncedFarms.add(record.id_farm_center);

              // Se era marcato per cancellazione, elimina definitivamente
              if (record._deleted) {
                await tableObj.delete(key);
                console.log(`${table}[${key}] eliminato definitivamente`);
              } else {
                // Altrimenti rimuovi solo il flag
                await tableObj.update(key, { _to_synch: false });
              }
            }
          }

          this.lastSyncTime = new Date();

          // Aggiorna la mappa per-azienda con server_time per le aziende toccate
          // così checkSyncStatus non riporta le proprie modifiche come "novità".
          // Non quelle con record rifiutati: se il server ha salvato altro di quell'azienda,
          // checkSyncStatus la riscarica e i rifiutati prendono la versione del server.
          if (res.server_time) {
            const map = { ...this.farmSyncMap };
            for (const fcId of syncedFarms) {
              if (!failedFarms.has(fcId)) map[fcId] = res.server_time;
            }
            saveFarmSyncMap(map);
            this.farmSyncMap = map;
          }
        }

        if (res.ok) {
          console.log(`Sincronizzati ${tablesToSync.length} tabelle`);
          // Segnala sync completato → le immagini in coda possono essere inviate ora
          window.dispatchEvent(new CustomEvent('poderi:sync-complete'));
          _perfLog(`syncToServer OK (${tablesToSync.length} tabelle)`, _t0); // [TEMP DEBUG]
          return { ok: true, synced: tablesToSync.length };
        }

        console.error('Errore sync dal server:', res.message, res.errors);

        let errors= res.errors || [];
        _perfLog('syncToServer FAIL (risposta server non ok)', _t0); // [TEMP DEBUG]
        return { ok: false, message: res.message, errors, failed };
      } catch (e) {
        console.error('Errore sync:', e);
        _perfLog('syncToServer FAIL (eccezione)', _t0); // [TEMP DEBUG]
        return { ok: false, message: e.message };
      } finally {
        this.syncInProgress = false;
      }
    },

    /**
     * Pianifica una sincronizzazione con debounce
     */
    scheduleSyncToServer() {
      if (this.syncTimer) {
        clearTimeout(this.syncTimer);
      }

      this.syncTimer = setTimeout(() => {
        this.syncToServer();
      }, this.syncDebounceMs);
    },

    /**
     * Applica i record ricevuti via SSE direttamente in Dexie (upsert/delete).
     * @param {Object} records - { table_name: [record, ...] }
     */
    async applyStreamRecords(records) {
      await this.ensureOpen();
      let applied = 0;
      let deleted = 0;

      for (const [tableName, tableRecords] of Object.entries(records)) {
        try {
          const tableObj = this.db.table(tableName);
          const primKey = tableObj.schema.primKey.keyPath;

          for (const record of tableRecords) {
            try {
              if (record._deleted) {
                // Delete
                const key = record[primKey];
                if (key) {
                  await tableObj.delete(key);
                  deleted++;
                }
              } else {
                // Upsert: espandi campo json come in syncDbFromBackend
                let rec = { ...record };
                if ("json" in rec) {
                  try {
                    let extra = rec.json;
                    if (typeof extra === "string") extra = JSON.parse(extra);
                    rec = { ...extra, ...rec };
                    delete rec.json;
                  } catch (e) {
                    console.warn(`SSE: errore parsing json per ${tableName}:`, e);
                  }
                }
                await tableObj.put(rec);
                applied++;
              }
            } catch (e) {
              console.warn(`SSE: errore applicazione record in ${tableName}:`, e);
            }
          }
        } catch (e) {
          console.warn(`SSE: tabella ${tableName} non trovata in Dexie, skip`);
        }
      }

      console.log(`SSE: applicati ${applied} upsert, ${deleted} delete`);
    },

    /**
     * Apre connessione SSE per ricevere notifiche di sync da altri utenti.
     * Passa token e selected_profile come query param perché EventSource
     * non supporta header custom (Authorization, X-Selected-Profile).
     */
    connectStream() {
      this.disconnectStream();

      // Costruisce URL con token e profilo per l'autenticazione server-side
      const params = new URLSearchParams();
      const token = localStorage.getItem('access_token');
      const selectedProfile = localStorage.getItem('selected_profile');
      if (token) params.set('token', token);
      if (selectedProfile) params.set('selected_profile', selectedProfile);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const url = `${BASE_URL}/api/v2/poderi2/farm_center/stream${qs}`;

      console.log('SSE: connecting...');

      const es = new EventSource(url, { withCredentials: true });
      this.streamConnection = markRaw(es);

      es.onopen = () => {
        const wasDisconnected = !this.streamConnected;
        console.log('SSE: connected');
        this.streamConnected = true;
        this.streamErrorCount = 0; // reset backoff

        // Se è una riconnessione, controlla dati persi durante la disconnessione
        if (wasDisconnected && this.db && this.isReady) {
          setTimeout(() => {
            if (!this.syncCheckInProgress) {
              console.log('SSE: reconnected, checking for missed updates...');
              this.checkSyncStatus();
            }
          }, 1000);
        }
      };

      es.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('SSE: received event', data);

          this.lastStreamEvent = data;
          this.streamEventCount++;

          if (data.type === 'sync_update') {
            // Ignora se l'evento proviene da questo stesso tab
            if (data.tab_id && data.tab_id === this.tabId) {
              console.log('SSE: sync_update ignorato (stesso tab)');
              return;
            }

            console.log(`SSE: sync_update ricevuto — tabelle: ${data.tables?.join(', ')}, farm_centers: ${data.farm_center_ids?.join(', ')}`);

            // Applica i record direttamente in Dexie (upsert/delete)
            if (data.records && this.db) {
              await this.applyStreamRecords(data.records);
            }

            // Aggiorna farmSyncMap con il server_time dell'evento SSE
            // Così il prossimo checkSyncStatus non riscarica dati già ricevuti via SSE
            if (data.server_time && data.farm_center_ids?.length) {
              const map = { ...this.farmSyncMap };
              for (const fcId of data.farm_center_ids) {
                map[fcId] = data.server_time;
              }
              saveFarmSyncMap(map);
              this.farmSyncMap = map;
              console.log('SSE: farmSyncMap aggiornata con server_time', data.server_time);
            }
          }
        } catch (e) {
          console.warn('SSE: error parsing event', e);
        }
      };

      es.onerror = () => {
        this.streamConnected = false;
        this.streamErrorCount++;

        // Chiudiamo sempre l'EventSource: il browser altrimenti riprova in autonomia
        // in modo aggressivo (anche ogni 300ms) senza che possiamo controllarlo.
        // Gestiamo noi il retry con exponential backoff.
        es.close();
        this.streamConnection = null;

        // Se l'app è in background (es. Capacitor minimize) non riconnettiamo subito:
        // il listener visibilitychange lo farà quando torna in primo piano.
        if (document.visibilityState === 'hidden') {
          console.log('SSE: error while hidden, will reconnect on focus');
          return;
        }

        // Exponential backoff: 2s → 4s → 8s → … → max 60s
        const delay = Math.min(
          this.streamReconnectDelay * Math.pow(2, this.streamErrorCount - 1),
          60000
        );
        console.log(`SSE: error #${this.streamErrorCount}, retry in ${delay}ms`);
        this.streamReconnectTimer = setTimeout(() => this.connectStream(), delay);
      };
    },

    /**
     * Chiude la connessione SSE.
     */
    disconnectStream() {
      if (this.streamReconnectTimer) {
        clearTimeout(this.streamReconnectTimer);
        this.streamReconnectTimer = null;
      }
      if (this.streamConnection) {
        this.streamConnection.close();
        this.streamConnection = null;
        this.streamConnected = false;
        console.log('SSE: disconnected');
      }
    },

    // ===== SYNC INTELLIGENTE =====

    /**
     * Controlla con il server se ci sono aziende cambiate.
     * Confronta il last_sync_at di ogni azienda (dal server) con la mappa locale.
     * Se trova differenze, riscarica le aziende cambiate.
     * Non richiede parametri — usa la mappa in localStorage e config salvati in startSyncWatchers.
     * @returns {Promise<Object>} - { ok, resynced, farmCenters }
     */
    async checkSyncStatus() {
      if (this.syncCheckInProgress) {
        return { ok: true, skipped: true, reason: 'already in progress' };
      }
      // Basta che il DB sia pronto (anche dopo page reload), non serve isSynced
      if (!this.db || !this.isReady) {
        return { ok: true, skipped: true, reason: 'db not ready' };
      }

      this.syncCheckInProgress = true;

      try {
        const res = await api.get('/api/v2/poderi2/sync_status');
        if (!res.ok) {
          console.warn('checkSyncStatus: errore risposta server', res.message);
          return { ok: false, message: res.message };
        }

        const serverTime = res.server_time;
        const localMap = this.farmSyncMap;

        // Confronto per-azienda: server last_sync_at vs mappa locale
        const changedIds = [];
        for (const fc of (res.farm_centers || [])) {
          const serverTs = fc.last_sync_at;
          const localTs = localMap[fc.id_farm_center];

          if (!localTs) {
            // Azienda mai vista localmente → va scaricata
            changedIds.push(fc.id_farm_center);
          } else if (serverTs && serverTs !== localTs) {
            // Timestamp diverso → qualcuno ha modificato
            changedIds.push(fc.id_farm_center);
          }
          // Se serverTs è null e localTs esiste → nessuna modifica via sync, ok
        }

        // Controlla se i vocabolari sono aggiornati.
        // Regola: stale se il server ha aggiornamenti più recenti del nostro locale
        //         OPPURE se il nostro locale ha >24h (hard timeout indipendente dal server).
        const VOCAB_MAX_AGE_MS = 24 * 60 * 60 * 1000;
        let vocabStale = false;
        const vocabSince = this.lastVocabSync;
        const lastSyncVoc = res.last_sync_voc;
        const refTime = serverTime ? new Date(serverTime) : new Date();
        const localTooOld = !vocabSince || (new Date(refTime) - new Date(vocabSince)) > VOCAB_MAX_AGE_MS;
        if (lastSyncVoc) {
          vocabStale = !vocabSince || new Date(lastSyncVoc) > new Date(vocabSince) || localTooOld;
        } else if (vocabSince && serverTime) {
          vocabStale = (new Date(serverTime) - new Date(vocabSince)) > VOCAB_MAX_AGE_MS;
        } else if (!vocabSince) {
          vocabStale = true;
        }

        if (changedIds.length === 0 && !vocabStale) {
          console.log('checkSyncStatus: tutto aggiornato');
          return { ok: true, resynced: 'none' };
        }

        console.log(`checkSyncStatus: ${changedIds.length} aziende cambiate, vocabStale=${vocabStale}`, changedIds);

        // Riscarica le aziende cambiate
        if (changedIds.length > 0) {
          await this.resyncFarmCenters(changedIds, vocabStale);
        } else if (vocabStale) {
          await this.resyncVocabularies();
        }

        // Aggiorna mappa locale con i nuovi timestamp dal server
        if (serverTime) {
          const map = { ...this.farmSyncMap };
          for (const fc of (res.farm_centers || [])) {
            map[fc.id_farm_center] = fc.last_sync_at || serverTime;
          }
          saveFarmSyncMap(map);
          this.farmSyncMap = map;

          if (vocabStale) {
            const vocabTs = lastSyncVoc || serverTime;
            localStorage.setItem(LS_LAST_VOCAB_SYNC, vocabTs);
            this.lastVocabSync = vocabTs;
          }
        }

        return { ok: true, resynced: changedIds.length > 0 ? 'partial' : 'vocab_only', farmCenters: changedIds, vocabRefreshed: vocabStale };
      } catch (e) {
        console.error('checkSyncStatus error:', e);
        return { ok: false, message: e.message };
      } finally {
        this.syncCheckInProgress = false;
      }
    },

    /**
     * Riscarica i dati di specifiche aziende (full download selettivo).
     * Cancella i record locali di quelle aziende e li riscarica dal server.
     * @param {string[]} farmCenterIds - lista id_farm_center da risincronizzare
     * @param {boolean} includeVocab - se true, riscarica anche i vocabolari
     */
    async resyncFarmCenters(farmCenterIds, includeVocab = false) {
      if (!farmCenterIds || farmCenterIds.length === 0) return;

      await this.ensureOpen();

      console.log(`resyncFarmCenters: riscarico ${farmCenterIds.length} aziende, includeVocab=${includeVocab}`);

      // SICUREZZA: prima di cancellare localmente, invia le modifiche non sincronizzate
      // al server per evitare perdita dati (es. record creati offline).
      const syncResult = await this.syncToServer();
      if (!syncResult.ok && !syncResult.skipped) {
        console.warn('resyncFarmCenters: syncToServer fallito, procedo comunque con filtro _to_synch');
      }
      // I record _to_synch (modifiche locali non ancora inviate: sync fallita per rete/500,
      // sync in corso, modifiche fatte durante il download) non vengono né cancellati né
      // sovrascritti dalla versione del server, che non le ha.
      // Eccezione: i record rifiutati dal server nel 207 (syncResult.failed) vengono
      // sovrascritti, altrimenti resterebbero da inviare per sempre; si perdono le modifiche
      // rifiutate. Un 207 senza failed (server non aggiornato) non dice quali sono: lì si
      // sovrascrivono tutti, come prima dell'ack per record.
      const legacy207 = !syncResult.ok && syncResult.errors?.length > 0 && !syncResult.failed;
      const pendingKeys = (tableName, records, primKey) => legacy207
        ? new Set()
        : new Set(records
          .filter(r => r._to_synch && !isRejected(syncResult.failed, tableName, r[primKey]))
          .map(r => r[primKey]));

      // 1. Scarica i dati dal server (solo le aziende selezionate)
      const params = { farm_centers: farmCenterIds.join(',') };
      if (includeVocab) params.include_vocab = 'true';
      const res = await api.get('/api/v2/poderi2/all_data', { params });

      if (!res.ok || !res.tables) {
        console.error('resyncFarmCenters: errore download', res.message);
        return;
      }

      // 2. Per ogni tabella, cancella i record delle aziende coinvolte e reinserisci
      const fcSet = new Set(farmCenterIds);

      for (const table of this.db.tables) {
        const tableName = table.name;
        const newRecords = res.tables[tableName];

        if (tableName === 'farm_center') {
          // Per farm_center: cancella solo quelle specifiche e reinserisci
          const pending = pendingKeys(tableName, (await table.bulkGet(farmCenterIds)).filter(Boolean), 'id_farm_center');
          for (const fcId of farmCenterIds) {
            if (pending.has(fcId)) continue;
            try { await table.delete(fcId); } catch (e) { /* potrebbe non esistere */ }
          }
          if (Array.isArray(newRecords) && newRecords.length > 0) {
            const expanded = this._expandJsonRecords(newRecords).filter(r => !pending.has(r.id_farm_center));
            if (expanded.length > 0) await table.bulkPut(expanded);
          }
          continue;
        }

        if (newRecords !== undefined) {
          try {
            const existing = await table.toArray();
            const primKey = table.schema.primKey.keyPath;
            const pending = pendingKeys(tableName, existing, primKey);
            const toDelete = existing
              .filter(r => r.id_farm_center && fcSet.has(r.id_farm_center) && !r._to_synch)
              .map(r => r[primKey]);

            // Distingue vocab (nessun id_farm_center) da dati aziendali
            const newHasFcId = Array.isArray(newRecords) && newRecords.some(r => r.id_farm_center);
            const existingHasFcId = existing.some(r => r.id_farm_center);
            const isVocabTable = !newHasFcId && !existingHasFcId;

            if (isVocabTable && includeVocab) {
              // Vocabolario: rimpiazzo completo (elimina anche record obsoleti)
              await table.clear();
              if (Array.isArray(newRecords) && newRecords.length > 0) {
                const expanded = this._expandJsonRecords(newRecords);
                await table.bulkAdd(expanded);
                console.log(`resyncFarmCenters: vocab ${tableName}: ${expanded.length} record`);
              }
            } else {
              // Dati aziendali: cancella solo i record delle aziende coinvolte
              if (toDelete.length > 0) {
                await table.bulkDelete(toDelete);
              }
              if (Array.isArray(newRecords) && newRecords.length > 0) {
                const expanded = this._expandJsonRecords(newRecords).filter(r => !pending.has(r[primKey]));
                if (expanded.length > 0) await table.bulkPut(expanded);
                console.log(`resyncFarmCenters: ${tableName}: ${expanded.length} record, ${newRecords.length - expanded.length} non sovrascritti (_to_synch)`);
              }
            }
          } catch (e) {
            console.error(`resyncFarmCenters: errore ${tableName}:`, e);
          }
        }
      }

      // Aggiorna mappa locale con i last_sync_at dei farm_center riscaricati
      if (res.server_time) {
        const map = { ...this.farmSyncMap };
        const fcRecords = res.tables?.farm_center || [];
        for (const fc of fcRecords) {
          map[fc.id_farm_center] = fc.last_sync_at || res.server_time;
        }
        saveFarmSyncMap(map);
        this.farmSyncMap = map;
      }

      console.log('resyncFarmCenters: completato');
    },

    /**
     * Espande il campo json nei record (helper riusabile)
     * @param {Array} records
     * @returns {Array}
     */
    _expandJsonRecords(records) {
      return records.map(rec => {
        if ("json" in rec) {
          try {
            let extra = rec.json;
            if (typeof extra === "string") extra = JSON.parse(extra);
            rec = { ...extra, ...rec };
            delete rec.json;
          } catch (e) { /* ignora errori parsing */ }
        }
        return rec;
      });
    },

    /**
     * Avvia i watcher per resync automatico (online, visibility, timer).
     * Chiamato dopo il primo sync completato.
     * Salva config e fetchDataFn per uso interno (forceFullResync).
     * @param {Object} options - { config, fetchDataFn }
     */
    startSyncWatchers({ config, fetchDataFn } = {}) {
      // Salva per uso interno (forceFullResync ne ha bisogno)
      this._syncConfig = config;
      this._fetchDataFn = fetchDataFn;

      if (this.onlineListenerAttached) return;
      this.onlineListenerAttached = true;

      const doCheck = async () => {
        if (this.syncCheckInProgress || !this.db || !this.isReady) return;
        await this.checkSyncStatus();
      };

      // Check immediato all'avvio: se il DB è vecchio >24h non aspettiamo 15 min
      setTimeout(async () => {
        if (this.syncCheckInProgress || !this.db || !this.isReady) return;
        const now = Date.now();
        const STALE_MS = 24 * 60 * 60 * 1000;
        const vocabAge = this.lastVocabSync
          ? now - new Date(this.lastVocabSync).getTime()
          : Infinity;
        const farmMap = this.farmSyncMap;
        // Cerca il farm con timestamp PIÙ VECCHIO (massima età), non il più recente
        const maxFarmAge = Object.values(farmMap).reduce((max, ts) => {
          const age = ts ? now - new Date(ts).getTime() : 0;
          return age > max ? age : max;
        }, 0);
        // Se non ci sono farm, non c'è niente da ricontrollare lato dati
        const dataAge = Object.keys(farmMap).length > 0 ? maxFarmAge : 0;
        if (vocabAge > STALE_MS || dataAge > STALE_MS) {
          console.log(`SyncWatcher: dati vecchi al boot (vocab=${Math.round(vocabAge/3600000)}h, data=${Math.round(dataAge/3600000)}h) — check immediato`);
          await doCheck();
        }
      }, 4000);

      // Al ritorno online
      this._onOnline = () => {
        console.log('SyncWatcher: online, checking sync status...');
        this.connectStream();
        // Re-invia eventuali record creati/modificati offline (il timer era scaduto senza rete)
        this.scheduleSyncToServer();
        setTimeout(doCheck, 2000);
      };
      window.addEventListener('online', this._onOnline);

      // Quando il tab torna visibile (dopo >5 min o dopo background Capacitor)
      this._lastVisibleTime = Date.now();
      this._onVisibility = () => {
        if (document.visibilityState === 'visible') {
          // Riconnetti SSE se era stata chiusa (es. errore in background)
          if (!this.streamConnected) {
            console.log('SyncWatcher: tornato visibile, riconnessione SSE...');
            this.streamErrorCount = 0; // reset backoff su ritorno volontario
            this.connectStream();
          }

          const elapsed = Date.now() - this._lastVisibleTime;
          if (elapsed > 5 * 60 * 1000) {
            console.log(`SyncWatcher: tab visibile dopo ${Math.round(elapsed / 60000)} min, checking...`);
            doCheck();
          }
        } else {
          this._lastVisibleTime = Date.now();
        }
      };
      document.addEventListener('visibilitychange', this._onVisibility);

      // Timer periodico ogni 15 minuti
      this.syncCheckTimer = setInterval(() => {
        if (document.visibilityState === 'visible') {
          console.log('SyncWatcher: periodic check');
          doCheck();
        }
      }, 15 * 60 * 1000);

      console.log('SyncWatchers: started (online + visibility + 15min timer)');
    },

    /**
     * Ferma i watcher di sync automatico
     */
    stopSyncWatchers() {
      if (this._onOnline) {
        window.removeEventListener('online', this._onOnline);
        this._onOnline = null;
      }
      if (this._onVisibility) {
        document.removeEventListener('visibilitychange', this._onVisibility);
        this._onVisibility = null;
      }
      if (this.syncCheckTimer) {
        clearInterval(this.syncCheckTimer);
        this.syncCheckTimer = null;
      }
      this.onlineListenerAttached = false;
      console.log('SyncWatchers: stopped');
    },

    /**
     * Forza un full resync completo (cancella tutto e riscarica).
     * Usa config e fetchDataFn salvati da startSyncWatchers.
     */
    async forceFullResync() {
      if (!this._syncConfig || !this._fetchDataFn) {
        console.error('forceFullResync: config/fetchDataFn non disponibili (startSyncWatchers non chiamato?)');
        return;
      }
      console.log('forceFullResync: avvio resync completo');
      await this.syncDbFromBackend(null, this._syncConfig, this._fetchDataFn);
    },

    /**
     * Riscarica solo i vocabolari (tabelle read-only).
     */
    async resyncVocabularies() {
      await this.ensureOpen();

      console.log('resyncVocabularies: riscarico vocabolari');
      const res = await api.get('/api/v2/poderi2/all_data', {
        params: { vocab_only: 'true' }
      });

      if (!res.ok || !res.tables) {
        console.error('resyncVocabularies: errore download', res.message);
        return;
      }

      // Aggiorna solo le tabelle read-only presenti nella risposta
      for (const table of this.db.tables) {
        const tableName = table.name;
        const newRecords = res.tables[tableName];
        if (!Array.isArray(newRecords) || newRecords.length === 0) continue;

        try {
          await table.clear();
          const expanded = this._expandJsonRecords(newRecords);
          await table.bulkAdd(expanded);
          console.log(`resyncVocabularies: ${tableName}: ${expanded.length} record`);
        } catch (e) {
          console.error(`resyncVocabularies: errore ${tableName}:`, e);
        }
      }

      // Aggiorna timestamp vocabolari
      if (res.server_time) {
        localStorage.setItem(LS_LAST_VOCAB_SYNC, res.server_time);
        this.lastVocabSync = res.server_time;
      }

      console.log('resyncVocabularies: completato');
    },

    async upsertVocab(table, record) {
      await this.ensureOpen()
      await this.db.table(table).put(record)
    },

    /**
     * Restituisce il conteggio dei record in attesa di sincronizzazione
     * @returns {Promise<number>}
     */
    async getPendingSyncCount() {
      if (!this.db || !this.isReady) return 0;

      // [TEMP DEBUG] guard anti-sovrapposizione: se una scansione precedente
      // è ancora in corso (es. setInterval che ha impiegato più del previsto),
      // non ne parte una seconda in parallelo. Ritorna l'ultimo valore noto.
      if (this.pendingSyncCountInProgress) {
        _perfLog('getPendingSyncCount SKIP (già in corso)', performance.now()); // [TEMP DEBUG]
        return this._lastPendingSyncCount ?? 0;
      }
      this.pendingSyncCountInProgress = true;
      const _t0 = performance.now(); // [TEMP DEBUG]

      let count = 0;
      try {
        for (const table of this.db.tables) {
          const records = await table.toArray();
          count += records.filter(r => r._to_synch).length;
        }
      } catch (e) { /* ignora */ }
      finally {
        this.pendingSyncCountInProgress = false;
        _perfLog(`getPendingSyncCount (${this.db.tables.length} tabelle)`, _t0); // [TEMP DEBUG]
      }
      this._lastPendingSyncCount = count;
      return count;
    },

    /**
     * Chiude il database
     */
    close() {
      this.disconnectStream();
      this.stopSyncWatchers();
      if (this.db) {
        this.db.close();
        this.isReady = false;
        console.log("DB chiuso");
      }
    },

    /**
     * Genera un GUID/UUID v4
     * @returns {string}
     */
    generateGuid() {
      // UUID v4 semplice (non crittografico)
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    },

    /**
     * Cancella completamente il database
     */
    async deleteDatabase() {
      try {
        if (this.db) {
          this.db.close();
        }
        await Dexie.delete("Poderi2DB");
        this.db = null;
        this.isReady = false;
        this.isSynced = false;
        this.isEmpty = true;
        setSyncStatus("idle");
        setRecoveryCount(0);
        localStorage.removeItem(LS_FARM_SYNC_MAP);
        localStorage.removeItem(LS_LAST_VOCAB_SYNC);
        this.farmSyncMap = {};
        this.lastVocabSync = null;
        console.log("Database cancellato");
        return { ok: true };
      } catch (e) {
        console.error("Errore cancellazione database:", e);
        return { ok: false, message: e.message };
      }
    },

    // ============================================
    // SOIL ANALYSIS METHODS
    // ============================================

    /**
     * Ottieni l'analisi del suolo per un plot specifico
     * @param {string} plotId
     * @param {string} farmCenterId
     * @returns {Promise<Object|null>}
     */
    async getSoilForPlot(plotId, farmCenterId) {
      try {
        await this.ensureOpen();
        
        // Trova tutte le analisi del farm center
        const soils = await this.select('soil', { id_farm_center: farmCenterId });
        
        if (!soils.ok || !soils.data) return null;
        
        // Prima cerca un'analisi specificamente associata a questo plot
        for (const soil of soils.data) {
          if (soil.id_plots && soil.id_plots.length > 0) {
            const plotIds = typeof soil.id_plots === 'string' 
              ? JSON.parse(soil.id_plots) 
              : soil.id_plots;
            
            if (plotIds.includes(plotId)) {
              return soil;
            }
          }
        }
        
        // Altrimenti ritorna quella default (se esiste)
        const defaultSoil = soils.data.find(s => s.is_default);
        return defaultSoil || null;
        
      } catch (e) {
        console.error('Errore getSoilForPlot:', e);
        return null;
      }
    },
  },

  getters: {
    /**
     * Ritorna true se il db è pronto
     */
    isDbReady: (state) => state.isReady,
  },
});

